import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Button from '@/components/base/Button';
import { api } from '@/utils/api';

/**
 * Forgot password (ForgotPasswordController::send, or the agency API's
 * auth/forgot-password since SCRUM-151 round 3): emails a reset link that works for 60 minutes. The reply is the same whether or not the email has an account.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSending(true);
    try {
      const reply = await api<{ message: string }>('/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(reply.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the reset link.');
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    'mt-1 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-100 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="page-rule mx-auto" aria-hidden="true" />
          <h1 className="mt-2 font-heading text-2xl font-semibold text-foreground-950">Reset your password</h1>
          <p className="mt-1 text-sm text-foreground-600">We&apos;ll email you a link to set a new one.</p>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          {sent ? (
            <div role="status" className="flex items-start gap-2 text-sm text-accent-800">
              <i className="ri-mail-check-line mt-0.5" />
              {sent}
            </div>
          ) : (
            <form onSubmit={(event) => void handleSubmit(event)}>
              {error ? (
                <div
                  role="alert"
                  className="mb-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800"
                >
                  <i className="ri-error-warning-line mt-0.5" />
                  {error}
                </div>
              ) : null}
              <label className="block text-sm font-medium text-foreground-800">
                Email
                <input
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@agency.com"
                  className={inputClass}
                />
              </label>
              <Button type="submit" size="lg" className="mt-6 w-full" disabled={sending}>
                {sending ? 'Sending…' : 'Email me a reset link'}
              </Button>
            </form>
          )}
          <div className="mt-4 text-center">
            <Link to="/login" className="text-xs font-medium text-foreground-500 hover:text-foreground-800">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
